import { useState, type ChangeEvent } from "react";

import { useAuth } from "src/hooks/use-auth";
import { useRouter } from "src/hooks/use-router";
import { slackHook } from "src/utils/snug/slackHooks";
import { useV3Create, GenericPath } from "src/utils/snug/estateAppApi";
import type { ProfessionalGroup } from "src/types/snugtotal";
import type { AuthContextType } from "src/contexts/auth/jwt-context";
import { SignupStepper } from "src/sections/starter-pay/signup-stepper";
import { writeStarterPayContext } from "src/sections/starter-pay/context";
import "./starter-pay.css";

const isGetSnugDomain =
  window.location.hostname.endsWith("getsnug.com") ||
  window.location.hostname === "localhost";

function LockIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect
        x="3"
        y="7"
        width="10"
        height="7"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M5.5 7V5a2.5 2.5 0 015 0v2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function GetStartedAccountPage() {
  const router = useRouter();
  const { signUp } = useAuth<AuthContextType>();
  const handleAddProGroup = useV3Create<ProfessionalGroup>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3,
  );

  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!isGetSnugDomain) return null;

  const validate = (): string | null => {
    if (!name.trim()) return "Please enter your full name.";
    if (!company.trim()) return "Please enter your firm name.";
    if (!email.trim() || !email.includes("@")) return "Please enter a valid email address.";
    if (password.length < 8) return "Password must be at least 8 characters.";
    if (password !== confirmPassword) return "Passwords do not match.";
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const userSignup = await signUp(email.trim().toLowerCase(), password, name.trim());
      const ud_id = userSignup?.userData?.ud_id || "";
      if (!ud_id) {
        throw new Error("Account created but could not be linked to your firm. Please contact support.");
      }
      const proGroup = await handleAddProGroup(
        { entity: "pro-group", ud_id },
        { name: company.trim() },
      );

      writeStarterPayContext({
        name: name.trim(),
        company: company.trim(),
        email: email.trim().toLowerCase(),
        plan: "annual",
        planLabel: "Starter",
        paymentSchedule: "single",
        proGroupId: proGroup?.id || "",
      });

      slackHook(
        `✅ GET STARTED — Account created\n` +
          `Name: ${name.trim()}\n` +
          `Email: ${email.trim()}\n` +
          `Company: ${company.trim()}`,
      );

      router.push("/get-started-verify");
    } catch (e: any) {
      const message =
        e?.response?.data?.message ||
        e?.message ||
        "Could not create your account. Please try again.";
      slackHook(
        `❌ GET STARTED — Account creation failed\n` +
          `Email: ${email}\n` +
          `Error: ${message}`,
      );
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sp-page">
      <div className="sp-brand">
        <img
          src="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/snug_logo.png"
          alt="Snug"
          className="sp-brand-logo"
        />
      </div>

      <section className="sp-inner">
        <h1 className="sp-headline">
          Get started with <em>Snug.</em>
        </h1>

        <p className="sp-sub">
          Includes full platform access, white-label setup, and onboarding
          support.
        </p>

        <SignupStepper currentStep="account" />

        <div className="sp-card">
          <div className="sp-card-head">
            <h2 className="sp-section-title">Create your account</h2>
            <p className="sp-section-help">
              Enter your details below to get started. You'll use your email and
              password to sign in going forward.
            </p>
          </div>
          <div className="sp-input-grid sp-input-grid-1">
            <label className="sp-input">
              <span className="sp-input-label">Full name</span>
              <input
                type="text"
                className="sp-input-field"
                value={name}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setName(e.target.value);
                  setError("");
                }}
                placeholder="Jane Smith"
                autoFocus
                autoComplete="name"
              />
            </label>
            <label className="sp-input">
              <span className="sp-input-label">Firm name</span>
              <input
                type="text"
                className="sp-input-field"
                value={company}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setCompany(e.target.value);
                  setError("");
                }}
                placeholder="Smith Financial"
                autoComplete="organization"
              />
            </label>
            <label className="sp-input">
              <span className="sp-input-label">Email</span>
              <input
                type="email"
                className="sp-input-field"
                value={email}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                placeholder="jane@smithfinancial.com"
                autoComplete="email"
              />
            </label>
            <label className="sp-input">
              <span className="sp-input-label">Password</span>
              <input
                type="password"
                className="sp-input-field"
                value={password}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setPassword(e.target.value);
                  setError("");
                }}
                placeholder="At least 8 characters"
                autoComplete="new-password"
              />
            </label>
            <label className="sp-input">
              <span className="sp-input-label">Confirm password</span>
              <input
                type="password"
                className="sp-input-field"
                value={confirmPassword}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setConfirmPassword(e.target.value);
                  setError("");
                }}
                placeholder="Re-enter your password"
                autoComplete="new-password"
              />
            </label>
          </div>
        </div>

        {error && <div className="sp-error">{error}</div>}

        <button
          type="button"
          className="sp-cta"
          disabled={loading}
          onClick={handleSubmit}
        >
          {loading ? (
            <span className="sp-cta-spinner" aria-hidden="true" />
          ) : (
            <LockIcon />
          )}
          Create my account
        </button>

        <p className="sp-disclaimer">
          Your password is encrypted in transit and at rest. We never store it
          in plain text.
        </p>
      </section>
    </div>
  );
}
